/**
 * Sanitizes an arbitrary label for use in a DICOM export filename.
 *
 * - Trims leading/trailing whitespace.
 * - Collapses internal whitespace runs to a single '-'.
 * - Replaces path-separator characters ('/' and '\') with '-' to prevent
 *   unintended directory structures or path-traversal when the filename is
 *   written to disk or sent in a multipart form boundary.
 * - Replaces ':' with '-' because colons are illegal in Windows filenames
 *   and cause extraction errors (0x80070057) when the ZIP is opened on Windows.
 * - Strips null bytes that some DICOM tag values may contain.
 */
export function sanitizeDicomLabel(label: string): string {
    return label
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[/\\:]/g, '-')
        .replace(/\0/g, '');
}

/**
 * Builds the canonical RS filename for a structure set export:
 * `RS_<sanitizedLabel>_<uid>.dcm`
 */
export function buildStructureSetFilename(label: string, uid: string): string {
    return `RS_${sanitizeDicomLabel(label)}_${uid}.dcm`;
}

/**
 * Builds the RTPLAN filename for a dose-plan export:
 * `RTPLAN_<sanitizedLabel>.dcm`
 */
export function buildRtPlanFilename(label: string): string {
    return `RTPLAN_${sanitizeDicomLabel(label)}.dcm`;
}

/**
 * Builds the RTDOSE filename for a dose export:
 * `RTDOSE_<sanitizedLabel>.dcm`
 */
export function buildRtDoseFilename(label: string): string {
    return `RTDOSE_${sanitizeDicomLabel(label)}.dcm`;
}

/**
 * Builds the REG filename for a rigid registration export:
 * `REG_<sanitizedLabel>.dcm`
 */
export function buildRegistrationFilename(label: string): string {
    return `REG_${sanitizeDicomLabel(label)}.dcm`;
}
